import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api } from "@/utils/api";
import type { AuthState, AuthProvider, AuthUser, AuthContextValue } from "@/types";

interface AuthStatusResponse {
  authenticated: boolean;
  provider?: "microsoft" | "google" | "email";
  calendarProvider?: "microsoft" | "google" | "apple" | null;
  user?: AuthUser;
}

const DEMO_USER: AuthUser = {
  name: "Vas Aditya",
  email: "vas.ady@mobilelive.ca",
  picture: undefined,
};

const AuthContext = createContext<AuthContextValue>({
  authState: "loading",
  user: null,
  provider: null,
  calendarProvider: null,
  login: () => {},
  loginWithEmail: async () => {},
  sendVerificationCode: async () => ({}),
  logout: () => {},
  checkSession: async () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [provider, setProvider] = useState<AuthProvider>(null);
  const [calendarProvider, setCalendarProvider] = useState<"microsoft" | "google" | "apple" | null>(null);

  useEffect(() => {
    api<{ success: boolean; data: AuthStatusResponse }>("/auth/status")
      .then((res) => {
        const payload = res.data;
        if (payload.authenticated) {
          setAuthState("loggedIn");
          setUser(payload.user || null);
          setProvider((payload.provider as AuthProvider) || null);
          setCalendarProvider(payload.calendarProvider || null);
        } else {
          setAuthState("loggedOut");
        }
      })
      .catch(() => setAuthState("loggedOut"));
  }, []);

  const login = useCallback((prov: "microsoft" | "google") => {
    // Demo mode: if backend is unreachable, log in with demo user
    if (prov === "demo" as any) {
      setUser(DEMO_USER);
      setProvider("microsoft");
      setAuthState("loggedIn");
      return;
    }
    const url = prov === "microsoft" ? "/auth/login" : "/auth/google/login";
    window.location.href = url;
  }, []);

  const sendVerificationCode = useCallback(async (email: string) => {
    try {
      const res = await api<{ success: boolean; devMode?: boolean; code?: string }>(
        "/auth/email/send-code",
        {
          method: "POST",
          body: JSON.stringify({ email, type: "signup" }),
        }
      );
      return { devMode: res.devMode, code: res.code };
    } catch (err) {
      console.error("Failed to send verification code:", err);
      throw err;
    }
  }, []);

  const loginWithEmail = useCallback(
    async (email: string, code: string, name?: string, password?: string) => {
      try {
        const res = await api<{ success: boolean; data: AuthStatusResponse }>(
          "/auth/email/verify",
          {
            method: "POST",
            body: JSON.stringify({ email, code, name, password }),
          }
        );
        if (res.data.authenticated) {
          setAuthState("loggedIn");
          setUser(res.data.user || null);
          setProvider((res.data.provider as AuthProvider) || "email");
          setCalendarProvider(res.data.calendarProvider || null);
        }
      } catch (err) {
        console.error("Failed to verify email code:", err);
        throw err;
      }
    },
    []
  );

  const checkSession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await api<{ success: boolean; data: AuthStatusResponse }>("/auth/status");
      return res.data.authenticated === true;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    if (!provider || authState === "loggedOut") return;
    // If in demo mode (no real session), just reset state
    if (user?.email === DEMO_USER.email) {
      setAuthState("loggedOut");
      setUser(null);
      setProvider(null);
      return;
    }
    window.location.href = "/auth/logout";
  }, [provider, authState, user]);

  return (
    <AuthContext.Provider
      value={{
        authState,
        user,
        provider,
        calendarProvider,
        login,
        loginWithEmail,
        sendVerificationCode,
        logout,
        checkSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
